import React, { useState, useEffect } from 'react';
import { useRouter } from 'expo-router';
import { StyleSheet, ScrollView, Platform, NativeModules, View, Modal, TouchableWithoutFeedback, Dimensions } from 'react-native';
import { useTranslation } from 'react-i18next';
import {
  Appbar,
  Button,
  Card,
  Dialog,
  Portal,
  Snackbar,
  Switch,
  Text,
  TextInput,
  useTheme,
  ActivityIndicator,
  Paragraph,
  Surface,
  Menu,
  List,
} from 'react-native-paper';
import { PhoneVerifiedBadge } from '@/src/features/phoneVerification';
import { CountryCodeSelector, COUNTRY_CODES } from '@/src/components/CountryCodeSelector';
import {
    getUserDetails,
    updateUserDetails,

    updateUserLanguage,
    changePassword,
    changePassword as apiChangePassword,
    requestEmailChange,
    getEmailChangeStatus,
} from '@/src/api/apiService';
import type {

    UserDetails as UserDetailsType,
    ApiResponseError,
    PasswordChangeErrorResponse,
    EmailChangeStatus,
} from '@/src/api/apiService';
import { getStoredLanguage, setStoredLanguage } from '@/src/lib/i18n';
import { OperatorLicenseInput } from '@/src/components/OperatorLicenseInput';
import { useProfile } from '@/src/hooks/useProfile';
import DateTimePicker, { useDefaultStyles } from 'react-native-ui-datepicker';
import NativeDateTimePicker from '@react-native-community/datetimepicker';
import { z } from 'zod';

// New password validation (same rules as reset-password)
const newPasswordSchema = z.object({
  password: z
    .string()
    .min(8, 'min8')
    .regex(/[A-Z]/, 'uppercase')
    .regex(/[a-z]/, 'lowercase')
    .regex(/[0-9]/, 'number'),
});

const formatDate = (dateString: string): string => {
  if (!dateString) return '';
  const date = new Date(dateString);
  return date.toLocaleDateString('pt-PT', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric'
  });
};

const formatDateToString = (date: Date | undefined | null): string => {
  if (!date) return '';
  return date.toISOString().split('T')[0];
};

const CustomDatePicker = ({ 
  label, 
  value, 
  onChange, 
  error, 
  maxDate 
}: { 
  label: string; 
  value: string; 
  onChange: (date: string) => void; 
  error?: boolean;
  maxDate?: string;
}) => {
  const [modalVisible, setModalVisible] = useState(false);
  const [nativeDatePickerShow, setNativeDatePickerShow] = useState(false);
  const [inputValue, setInputValue] = useState(formatDate(value));
  const theme = useTheme();
  const isDarkMode = theme.dark;
  const defaultStyles = useDefaultStyles();
  
  // Get theme colors for native date picker
  const androidAccentColor = theme.colors.primary;
  const androidBackgroundColor = isDarkMode ? '#333333' : '#FFFFFF';
  const androidHeaderBackgroundColor = theme.colors.primary;
  const androidHeaderTextColor = '#FFFFFF';
  const androidTextColor = isDarkMode ? '#FFFFFF' : '#000000';
  
  // Customize styles based on theme
  const themedStyles = {
    ...defaultStyles,
    day: { 
      ...defaultStyles.day,
      backgroundColor: isDarkMode ? '#444' : '#f5f5f5' 
    },
    today: { 
      ...defaultStyles.today,
      borderColor: theme.colors.primary 
    },
    selected: { 
      ...defaultStyles.selected,
      backgroundColor: theme.colors.primary 
    },
    selected_label: { 
      ...defaultStyles.selected_label,
      color: '#fff' 
    },
  };

  // Parse the date value as a Date object
  const dateValue = value ? new Date(value) : undefined;

  // Update inputValue when value changes from props
  useEffect(() => {
    setInputValue(formatDate(value));
  }, [value]);
  
  // Handle native date picker change (Android/iOS)
  const onNativeDateChange = (event: any, selectedDate?: Date) => {
    setNativeDatePickerShow(false);
    
    if (event.type === 'dismissed') {
      return; // User canceled the date picker
    }

    if (selectedDate) {
      onChange(formatDateToString(selectedDate));
    }
  };

  // Handle manual text input - only allow numbers and auto-format with /
  const handleTextInputChange = (text: string) => {
    // Remove any non-numeric characters
    const numericText = text.replace(/[^0-9]/g, '');
    
    // Format with / characters automatically
    let formattedText = '';
    
    if (numericText.length <= 2) {
      formattedText = numericText;
    } else if (numericText.length <= 4) {
      formattedText = numericText.slice(0, 2) + '/' + numericText.slice(2);
    } else {
      formattedText = numericText.slice(0, 2) + '/' + numericText.slice(2, 4) + '/' + numericText.slice(4, 8);
    }
    
    setInputValue(formattedText);
  };

  // Parse user input date in format DD/MM/YYYY
  const parseUserInputDate = (input: string): Date | null => {
    // Try to parse the date - now we expect format DD/MM/YYYY
    const dateRegex = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/;
    const match = input.match(dateRegex);
    
    if (match) {
      const day = parseInt(match[1], 10);
      const month = parseInt(match[2], 10) - 1; // Month is 0-indexed in Date
      const year = parseInt(match[3], 10);
      
      // Create a date and verify it's valid
      const date = new Date(year, month, day);
      if (
        date.getFullYear() === year &&
        date.getMonth() === month &&
        date.getDate() === day
      ) {
        // Check if date is within allowed range
        if (maxDate && date > new Date(maxDate)) {
          return null;
        }
        return date;
      }
    }
    return null;
  };

  // Validate the entered date when input field loses focus
  const handleInputBlur = () => {
    const parsedDate = parseUserInputDate(inputValue);
    if (parsedDate) {
      // Valid date format
      onChange(formatDateToString(parsedDate));
    } else {
      // Invalid date format, revert to current value
      setInputValue(formatDate(value));
    }
  };

  return (
    <>
      <TextInput
        label={label}
        mode="outlined"
        value={inputValue}
        style={{ marginBottom: 10 }}
        editable={true}
        error={error}
        onChangeText={handleTextInputChange}
        onBlur={handleInputBlur}
        placeholder="DD/MM/YYYY"
        keyboardType="numeric"
        maxLength={10} // Max length is 10: DD/MM/YYYY
        right={<TextInput.Icon 
          icon="calendar" 
          onPress={() => {
            if (Platform.OS === 'web') {
              setModalVisible(true);
            } else {
              setNativeDatePickerShow(true);
            }
          }} 
        />}
      />

      {Platform.OS === 'web' ? (
        // Web date picker using react-native-ui-datepicker
        <Modal
          animationType="fade"
          transparent={true}
          visible={modalVisible}
          onRequestClose={() => setModalVisible(false)}
        >
          <TouchableWithoutFeedback onPress={() => setModalVisible(false)}>
            <View style={{
              flex: 1,
              justifyContent: 'center',
              alignItems: 'center',
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
            }}>
              <TouchableWithoutFeedback>
                <View style={{
                  backgroundColor: '#fff',
                  borderRadius: 10,
                  padding: 20,
                  width: '80%',
                  maxWidth: 400,
                }}>
                  <DateTimePicker
                    mode="single"
                    date={dateValue}
                    onChange={({ date }) => {
                      if (date) {
                        // The library returns either a string, Date or timestamp
                        const dateString = typeof date === 'string'
                          ? date
                          : date instanceof Date
                            ? formatDateToString(date)
                            : typeof date === 'number'
                              ? formatDateToString(new Date(date))
                              : '';
                        
                        onChange(dateString);
                        setModalVisible(false);
                      }
                    }}
                    maxDate={maxDate ? new Date(maxDate) : undefined}
                    style={{ backgroundColor: isDarkMode ? '#333' : '#fff' }}
                    styles={themedStyles}
                    locale="en"
                  />
                </View>
              </TouchableWithoutFeedback>
            </View>
          </TouchableWithoutFeedback>
        </Modal>
      ) : (
        // Native date picker for iOS/Android
        nativeDatePickerShow && (
          <NativeDateTimePicker
            value={dateValue || new Date()}
            mode="date"
            display={Platform.OS === 'ios' ? 'spinner' : 'default'}
            onChange={onNativeDateChange}
            maximumDate={maxDate ? new Date(maxDate) : undefined}
            // iOS styling
            accentColor={theme.colors.primary}
            themeVariant={isDarkMode ? 'dark' : 'light'}
            textColor={isDarkMode ? '#FFFFFF' : '#000000'}
            // Android styling
            style={{ 
              backgroundColor: Platform.OS === 'android' ? androidBackgroundColor : undefined
            }}
            // Android theme customization - only applied on Android
            {...(Platform.OS === 'android' ? {
              theme: {
                backgroundColor: androidBackgroundColor,
                headerBackgroundColor: androidHeaderBackgroundColor,
                headerTextColor: androidHeaderTextColor,
                textColor: androidTextColor,
                accentColor: androidAccentColor,
                calendarTextColor: androidTextColor,
                calendarBackgroundColor: androidBackgroundColor,
                dayColor: androidTextColor,
                selectedDayColor: '#FFFFFF',
                selectedDayBackgroundColor: androidAccentColor,
              }
            } : {})}
          />
        )
      )}
    </>
  );
};

const UserDataScreen = () => {
  const { t, i18n } = useTranslation();
  const theme = useTheme();
  const router = useRouter();
  const styles = createStyles(theme);
  const { width: SCREEN_WIDTH } = Dimensions.get('window');
  const isLargeScreen = SCREEN_WIDTH > 1024;

  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [snackbarVisible, setSnackbarVisible] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [showLanguageMenu, setShowLanguageMenu] = useState(false);
  const [countryCode, setCountryCode] = useState('+351');
  const [phoneMenuVisible, setPhoneMenuVisible] = useState(false);



  // User Profile State - UserDetailsType from apiService will be the shape
  const [userData, setUserData] = useState<UserDetailsType | null>(null);
  const [isEditingProfile, setIsEditingProfile] = useState(false);
  const [profileForm, setProfileForm] = useState<any>({}); // For editing
  const [profileErrors, setProfileErrors] = useState<{
    fullName?: string;
    email?: string;
    phoneNumber?: string;
    dateOfBirth?: string;
    nif?: string;
    address?: string;
    licenseNumber?: string;
    non_field_errors?: string[];
    detail?: string;
    other?: string[];
  }>({});




  // Change password state (visible for both driver and owner)
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword1: '',
    newPassword2: '',
  });
  const [passwordFormErrors, setPasswordFormErrors] = useState<{
    currentPassword?: string;
    newPassword1?: string;
    newPassword2?: string;
  }>({});
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword1, setShowNewPassword1] = useState(false);
  const [showNewPassword2, setShowNewPassword2] = useState(false);
  const [isChangingPassword, setIsChangingPassword] = useState(false);

  // Two-step email change state (#188): requesting a change never mutates
  // userData.email immediately — it only updates after the user clicks the
  // confirmation link sent to the new address.
  const [emailChangeStatus, setEmailChangeStatus] = useState<EmailChangeStatus | null>(null);
  const [newEmailInput, setNewEmailInput] = useState('');
  const [emailChangeError, setEmailChangeError] = useState<string | undefined>(undefined);
  const [isRequestingEmailChange, setIsRequestingEmailChange] = useState(false);
  const [emailChangeCooldown, setEmailChangeCooldown] = useState(0);

  // Initialize currentLanguage state with the correct format from i18n
  const [currentLanguage, setCurrentLanguage] = useState(i18n.language);

  const { hasDriver } = useProfile(userData?.groups || []);

  // Get system language
  const getSystemLanguage = () => {
    const deviceLanguage = 
      Platform.OS === 'ios'
        ? NativeModules.SettingsManager.settings.AppleLocale ||
          NativeModules.SettingsManager.settings.AppleLanguages[0]
        : NativeModules.I18nManager.localeIdentifier;
    
    // Convert to our supported format (lowercase)
    const languageCode = deviceLanguage.split('_')[0].toLowerCase();
    if (languageCode === 'pt') return 'pt-pt'; // Default specific Portuguese to pt-pt
    return languageCode;
  };

  useEffect(() => {
    const loadAllData = async () => {
      setIsLoading(true);
      try {
        // Language already initialized in RootLayout; just sync local state
        setCurrentLanguage(i18n.language);

        const userDetailsResponse = await getUserDetails();
        
        if (userDetailsResponse.ok && userDetailsResponse.data) {
          const apiUserData = userDetailsResponse.data;
          setUserData(apiUserData);
          setProfileForm({
            fullName: apiUserData.full_name || '',
            email: apiUserData.email || '',
            phoneNumber: apiUserData.phone_number || '', 
            dateOfBirth: apiUserData.profile?.date_of_birth || '',
            nif: apiUserData.profile?.nif || '',
            address: apiUserData.profile?.address || '',
            licenseNumber: apiUserData.profile?.license_number || '',
          });

          // Parse phone number into country code and local part (match company.tsx behavior)
          const phone = apiUserData.phone_number || '';
          if (phone && phone.startsWith('+')) {
            const candidate = COUNTRY_CODES
              .filter(code => phone.startsWith(code))
              .sort((a, b) => b.length - a.length)[0];
            if (candidate) {
              setCountryCode(candidate);
              const local = phone.slice(candidate.length).replace(/\D/g, '').slice(0, 15);
              setProfileForm((prev: any) => ({ ...prev, phoneNumber: local }));
            } else {
              const m = phone.match(/^(\+\d{1,4})(.*)$/);
              setCountryCode(m ? m[1] : '+');
              const local = (m ? m[2] : phone.replace('+', '')).replace(/\D/g, '').slice(0, 15);
              setProfileForm((prev: any) => ({ ...prev, phoneNumber: local }));
            }
          } else {
            // No plus sign: keep as provided, normalized to digits and max 15
            const local = (phone || '').replace(/\D/g, '').slice(0, 15);
            setProfileForm((prev: any) => ({ ...prev, phoneNumber: local }));
          }
        } else {
          const errorData = userDetailsResponse.data as ApiResponseError;
          throw new Error(errorData?.detail || errorData?.message || 'Failed to fetch user details');
        }


      } catch (error: any) {
        console.error("Failed to load data:", error);
        setSnackbarMessage(error.message || t('userData.loadError', 'Failed to load data.'));
        setSnackbarVisible(true);
      }
      setIsLoading(false);
    };
    loadAllData();

    // Restore "waiting for confirmation" state if the user left and came back.
    (async () => {
      const statusResponse = await getEmailChangeStatus();
      if (statusResponse.ok && statusResponse.data) {
        setEmailChangeStatus(statusResponse.data);
      }
    })();
  }, []);

  // Tick down the 1/min resend cooldown for the email-change card.
  useEffect(() => {
    if (emailChangeCooldown <= 0) return;
    const interval = setInterval(() => {
      setEmailChangeCooldown((prev) => (prev > 0 ? prev - 1 : 0));
    }, 1000);
    return () => clearInterval(interval);
  }, [emailChangeCooldown]);

  const handleRequestEmailChange = async () => {
    setEmailChangeError(undefined);
    const candidate = (newEmailInput || '').trim().toLowerCase();
    if (!candidate || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(candidate)) {
      setEmailChangeError(t('userData.emailInvalid', 'Enter a valid email address.'));
      return;
    }

    setIsRequestingEmailChange(true);
    try {
      const result = await requestEmailChange(candidate);
      if (result.ok) {
        setEmailChangeStatus({ pending: true, new_email: candidate, rate_limited: true });
        setEmailChangeCooldown(60);
        setNewEmailInput('');
        setSnackbarMessage(t('userData.emailChangeRequestSuccess', "If the request is valid, we've sent a confirmation email to the new address."));
        setSnackbarVisible(true);
      } else if (result.status === 429) {
        setEmailChangeCooldown(60);
        setEmailChangeError(t('userData.emailChangeRateLimited', 'Please wait a moment before trying again.'));
      } else {
        const data = result.data as ApiResponseError;
        setEmailChangeError(
          (Array.isArray((data as any)?.new_email) ? (data as any).new_email[0] : undefined) ||
          data?.detail ||
          t('userData.emailChangeError', 'Failed to request email change.')
        );
      }
    } catch (error: any) {
      setEmailChangeError(error.message || t('userData.emailChangeError', 'Failed to request email change.'));
    }
    setIsRequestingEmailChange(false);
  };

  const handleProfileInputChange = (name: string, value: string | boolean | number) => {
    setProfileForm((prev: any) => ({ ...prev, [name]: value }));
    // Clear field-specific error on change
    if (profileErrors[name as keyof typeof profileErrors]) {
      setProfileErrors((prev) => ({ ...prev, [name]: undefined }));
    }
  };




  const handleSaveProfile = async () => {
    setIsSubmitting(true);
    try {
        // Create a profile object with only changed fields
        const changedProfileFields: any = {};
        if (profileForm.dateOfBirth !== userData?.profile?.date_of_birth) {
            changedProfileFields.date_of_birth = profileForm.dateOfBirth || null;
        }
        if (profileForm.nif !== userData?.profile?.nif) {
            changedProfileFields.nif = profileForm.nif || null;
        }
        if (profileForm.address !== userData?.profile?.address) {
            changedProfileFields.address = profileForm.address || null;
        }
        if (profileForm.licenseNumber !== userData?.profile?.license_number) {
            changedProfileFields.license_number = profileForm.licenseNumber || null;
        }

        // Create the user details payload with only changed fields.
        // Email is NOT included here: it can only be changed via the
        // dedicated two-step email-change flow (#188), never through this
        // generic profile PATCH.
        const userDetailsPayload: any = {};
        if (profileForm.fullName !== userData?.full_name) {
            userDetailsPayload.full_name = profileForm.fullName;
        }
        // Recompose full phone number with selected country code; enforce max 15 digits for local part
        const composedPhoneNumber = (profileForm.phoneNumber || '').replace(/\D/g, '').slice(0, 15);
        const previousPhoneRaw = (userData?.phone_number || '');
        // Compare normalized previous phone (without spaces) to avoid unnecessary updates
        const previousNormalized = previousPhoneRaw.replace(/\s/g, '');
        const nextFullPhone = composedPhoneNumber ? `${countryCode}${composedPhoneNumber}` : null;
        if ((nextFullPhone || '') !== previousNormalized) {
            userDetailsPayload.phone_number = nextFullPhone;
        }

        // Always include profile if there are changed fields or license number
        if (Object.keys(changedProfileFields).length > 0 || profileForm.licenseNumber) {
            userDetailsPayload.profile = changedProfileFields;
        }

        const userUpdateResult = await updateUserDetails(userDetailsPayload);

        if (userUpdateResult.ok && userUpdateResult.data) {
            const updatedApiUserData = userUpdateResult.data;
            setUserData(updatedApiUserData);
            setProfileErrors({});

            // Re-parse phone number like on load to avoid duplicating country code in the input
            const updatedPhone = updatedApiUserData.phone_number || '';
            let nextCountry = countryCode;
            let localDigits = '';
            if (updatedPhone && updatedPhone.startsWith('+')) {
                const candidate = COUNTRY_CODES
                  .filter(code => updatedPhone.startsWith(code))
                  .sort((a, b) => b.length - a.length)[0];
                if (candidate) {
                  nextCountry = candidate;
                  localDigits = updatedPhone.slice(candidate.length).replace(/\D/g, '').slice(0, 15);
                } else {
                  const m = updatedPhone.match(/^(\+\d{1,4})(.*)$/);
                  nextCountry = m ? m[1] : '+';
                  localDigits = (m ? m[2] : updatedPhone.replace('+', '')).replace(/\D/g, '').slice(0, 15);
                }
            } else {
                localDigits = (updatedPhone || '').replace(/\D/g, '').slice(0, 15);
            }
            setCountryCode(nextCountry);

            setProfileForm({
                fullName: updatedApiUserData.full_name || '',
                email: updatedApiUserData.email || '',
                phoneNumber: localDigits,
                dateOfBirth: updatedApiUserData.profile?.date_of_birth || '',
                nif: updatedApiUserData.profile?.nif || '',
                address: updatedApiUserData.profile?.address || '',
                licenseNumber: updatedApiUserData.profile?.license_number || '',
            });
            setIsEditingProfile(false);

            setSnackbarMessage(t('userData.profileUpdateSuccess', 'Profile updated successfully.'));
            setSnackbarVisible(true);
        } else {
            const data: any = userUpdateResult.data || {};
            const newErrors: any = {};
            const setFirst = (arr: any) => Array.isArray(arr) ? String(arr[0]) : String(arr);

            // Top-level fields
            if (data.email) newErrors.email = setFirst(data.email);
            if (data.phone_number) newErrors.phoneNumber = setFirst(data.phone_number);
            if (data.non_field_errors) newErrors.non_field_errors = Array.isArray(data.non_field_errors) ? data.non_field_errors.map(String) : [String(data.non_field_errors)];
            if (data.detail) newErrors.detail = String(data.detail);

            // Nested profile fields
            if (data.profile && typeof data.profile === 'object' && !Array.isArray(data.profile)) {
              const p = data.profile;
              if (p.date_of_birth) newErrors.dateOfBirth = setFirst(p.date_of_birth);
              if (p.nif) newErrors.nif = setFirst(p.nif);
              if (p.address) newErrors.address = setFirst(p.address);
              if (p.license_number) newErrors.licenseNumber = setFirst(p.license_number);
            }

            // Capture any other unknown keys as generic messages
            const known = new Set(['email','phone_number','profile','non_field_errors','detail']);
            const others: string[] = [];
            Object.keys(data).forEach(k => {
              if (!known.has(k)) {
                const v = data[k];
                if (Array.isArray(v)) others.push(...v.map(String)); else if (v) others.push(String(v));
              }
            });
            if (others.length) newErrors.other = others;

            setProfileErrors(newErrors);

            // Also surface a concise snackbar for quick visibility
            const generalMsg = newErrors.detail || (newErrors.non_field_errors && newErrors.non_field_errors.join('\n')) || (newErrors.other && newErrors.other.join('\n'));
            setSnackbarMessage(generalMsg || t('userData.updateError', 'Failed to update profile.'));
            setSnackbarVisible(true);
        }
    } catch (error: any) {      
        setSnackbarMessage(error.message || t('userData.updateError', 'An unexpected error occurred.'));
        setSnackbarVisible(true);
    }
    setIsSubmitting(false);
  };




  const validateNewPassword = (password: string): string | undefined => {
    try {
      newPasswordSchema.parse({ password });
      return undefined;
    } catch (err) {
      if (err instanceof z.ZodError && err.issues[0]) {
        const code = err.issues[0].message;
        if (code === 'min8') return t('passwordTooShort', 'Password must be at least 8 characters');
        if (code === 'uppercase') return t('passwordMustContainUppercase', 'Password must contain at least one uppercase letter');
        if (code === 'lowercase') return t('passwordMustContainLowercase', 'Password must contain at least one lowercase letter');
        if (code === 'number') return t('passwordMustContainNumber', 'Password must contain at least one number');
      }
      return t('passwordValidationError', 'Password validation error');
    }
  };

  const handleChangePasswordSubmit = async () => {
    setPasswordFormErrors({});
    const { currentPassword, newPassword1, newPassword2 } = passwordForm;
    if (!currentPassword?.trim()) {
      setPasswordFormErrors((prev) => ({ ...prev, currentPassword: t('passwordRequired', 'Password is required') }));
      return;
    }
    if (!newPassword1?.trim()) {
      setPasswordFormErrors((prev) => ({ ...prev, newPassword1: t('passwordRequired', 'Password is required') }));
      return;
    }
    const newPassword1Error = validateNewPassword(newPassword1);
    if (newPassword1Error) {
      setPasswordFormErrors((prev) => ({ ...prev, newPassword1: newPassword1Error }));
      return;
    }
    if (newPassword1 !== newPassword2) {
      setPasswordFormErrors((prev) => ({ ...prev, newPassword2: t('passwordsDontMatch', 'Passwords do not match') }));
      return;
    }
    setIsChangingPassword(true);
    try {
      const result = await changePassword(currentPassword, newPassword1, newPassword2);
      if (result.ok) {
        setPasswordForm({ currentPassword: '', newPassword1: '', newPassword2: '' });
        setPasswordFormErrors({});
        setSnackbarMessage(t('passwordChangeSuccess', 'Your password has been changed successfully.'));
        setSnackbarVisible(true);
      } else {
        const data = result.data as PasswordChangeErrorResponse;
        const errors: typeof passwordFormErrors = {};
        const setFirst = (arr: unknown) => (Array.isArray(arr) ? String(arr[0]) : arr ? String(arr) : '');
        if (data?.old_password) errors.currentPassword = setFirst(data.old_password);
        if (data?.new_password1) errors.newPassword1 = setFirst(data.new_password1);
        if (data?.new_password2) errors.newPassword2 = setFirst(data.new_password2);
        if (data?.detail && !errors.currentPassword && !errors.newPassword1 && !errors.newPassword2) {
          setSnackbarMessage(data.detail);
          setSnackbarVisible(true);
        }
        setPasswordFormErrors(errors);
      }
    } catch (error: any) {
      setSnackbarMessage(error.message || t('passwordResetError', 'An error occurred while changing your password.'));
      setSnackbarVisible(true);
    }
    setIsChangingPassword(false);
  };
  
  const renderDetailItem = (label: string, value: string | number | undefined | boolean, isSwitch = false) => {
    const displayValue = value === null || typeof value === 'undefined' || value === '' ? t('userData.notAvailable', 'N/A') : String(value);
    if (isSwitch) {
        return (
            <Surface style={[styles.detailItem, styles.switchContainerView]} elevation={0}>
                <Text variant="bodyLarge" style={styles.detailLabel}>{label}</Text>
                <Switch value={Boolean(value)} disabled={true} />
            </Surface>
        );
    }
    return (
        <Surface style={styles.detailItem} elevation={0}>
            <Text variant="bodyLarge" style={styles.detailLabel}>{label}:</Text>
            <Text variant="bodyLarge" style={styles.detailValue}>{displayValue}</Text>
        </Surface>
    );
  };

  // Update language change handler
  const handleLanguageChange = async (language: string) => {
    // Use the provided language code directly
    const formattedLanguage = language;
    try {
      console.log('[UserDataScreen] Changing language to:', formattedLanguage);
      console.log('[UserDataScreen] Current i18n language before change:', i18n.language);
      
      // Update the language in the backend (API might expect specific casing, adjust if needed)
      const apiResponse = await updateUserLanguage(formattedLanguage); // Pass lowercase to API
      if (!apiResponse.ok) {
        console.error('[UserDataScreen] API response not ok:', apiResponse);
        const errorData = apiResponse.data as ApiResponseError;
        const errorMessage = errorData?.detail || errorData?.message || 'Failed to update language in backend';
        throw new Error(errorMessage);
      }
      
      // Store the preference locally (ensure consistent casing)
      await setStoredLanguage(formattedLanguage);
      console.log('[UserDataScreen] Language stored locally successfully.');
      
      // Change the language using i18n. This will trigger the listener in RootLayout.
      await i18n.changeLanguage(formattedLanguage);
      console.log('[UserDataScreen] i18n.changeLanguage called. New language should be:', i18n.language);
      
      // Update the local component state for the segmented button (ensure consistent casing)
      setCurrentLanguage(formattedLanguage);
      console.log('[UserDataScreen] Local currentLanguage state updated to:', formattedLanguage);
      
      setSnackbarMessage(t('userData.languageChanged', 'Language changed successfully.'));
      setSnackbarVisible(true);

      // No need to force re-render with router.replace or component keys anymore
      // The I18nextProvider key change in RootLayout should handle this.

      console.log('[UserDataScreen] Available translations after change for language (' + formattedLanguage + '):', Object.keys(i18n.getResourceBundle(formattedLanguage, 'translation') || {}));
    } catch (error: any) {
      console.error('[UserDataScreen] Error changing language:', error);
      const message = error.message || t('userData.languageChangeError', 'Failed to change language.');
      setSnackbarMessage(message);
      setSnackbarVisible(true);

      // If backend update failed, or i18n change failed, consider reverting UI
      console.warn('[UserDataScreen] Attempting to revert local currentLanguage state if necessary.');
      setCurrentLanguage(i18n.language); // Revert button to actual current i18n lang
    }
  };

  // Add useEffect to monitor language changes from i18n instance
  useEffect(() => {
    const onLanguageChanged = (lng: string) => {
      console.log('[UserDataScreen] i18n languageChanged event detected:', lng);
      if (currentLanguage !== lng) {
        setCurrentLanguage(lng);
        console.log('[UserDataScreen] currentLanguage state updated from i18n event to:', lng);
      }
    };
    i18n.on('languageChanged', onLanguageChanged);
    return () => {
      i18n.off('languageChanged', onLanguageChanged);
    };
  }, [currentLanguage]); // Rerun if currentLanguage changes to avoid stale closure

  const getCurrentLanguage = () => {
    switch (currentLanguage) {
      case 'pt':
        return '🇵🇹 PT';
      case 'pt_BR':
        return '🇧🇷 BR';
      default:
        return '🇬🇧 EN';
    }
  };


  if (isLoading) {
    return (
      <Surface style={styles.centered}>
        <ActivityIndicator animating={true} size="large" />
        <Text style={{ marginTop: 10 }}>{t('loading', 'Loading data...')}</Text>
      </Surface>
    );
  }

  return (
    <Surface style={{ flex: 1 }}>
      <Appbar.Header style={{ backgroundColor: theme.colors.surfaceVariant }}>
        <Appbar.BackAction onPress={() => router.back()} />
        <Appbar.Content title={t('userData.title', 'User Data')} />
      </Appbar.Header>
      <ScrollView style={styles.container} contentContainerStyle={styles.contentContainer}>
        {/* Language Section */}
        <Card style={[styles.card, { elevation: 0 }]}>
          <Card.Content>
            <Text variant="headlineMedium">{t('userData.languageTitle', 'Language Settings')}</Text>
            <Text variant="titleMedium" style={styles.sectionSubTitle}>{t('userData.currentLanguage', 'Current Language')}</Text>
            <Menu
              visible={showLanguageMenu}
              onDismiss={() => setShowLanguageMenu(false)}
              anchor={
                <Button
                  mode="text"
                  onPress={() => setShowLanguageMenu(true)}
                  style={styles.languageButton}
                >
                  {getCurrentLanguage()}
                </Button>
              }
            >
              <Menu.Item
                onPress={() => handleLanguageChange('en')}
                title={t('languages.english', '🇬🇧 English')}
                style={styles.menuItem}
              />
              <Menu.Item
                onPress={() => handleLanguageChange('pt')}
                title={t('languages.portuguese', '🇵🇹 Português')}
                style={styles.menuItem}
              />
              <Menu.Item
                onPress={() => handleLanguageChange('pt_BR')}
                title={t('languages.portugueseBR', '🇧🇷 Português (BR)')}
                style={styles.menuItem}
              />
            </Menu>
          </Card.Content>
        </Card>

        {/* Profile Section */}
        <Card style={[styles.card, { elevation: 0 }]}>
          <Card.Content>
            <Text variant="headlineMedium">{t('userData.profileTitle', 'My Profile')}</Text>
            {isEditingProfile ? (
              <>
                <TextInput 
                  label={t('userData.fullName', 'Full Name')} 
                  value={profileForm.fullName || ''} 
                  onChangeText={(text) => handleProfileInputChange('fullName', text)} 
                  style={styles.input}
                  mode="flat"
                />
                <TextInput
                  label={t('userData.email', 'Email')}
                  value={userData?.email || ''}
                  editable={false}
                  style={styles.input}
                  mode="flat"
                />
                <Text style={[styles.warningText, { color: theme.colors.onSurfaceVariant }]}>
                  {t('userData.emailEditHint', 'To change your email, use the "Change Email" section below.')}
                </Text>
                <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
                  <Button
                    mode="outlined"
                    onPress={() => setPhoneMenuVisible(true)}
                    style={{ minWidth: 90 }}
                  >
                    {countryCode}
                  </Button>
                  <TextInput 
                    label={t('userData.phoneNumber', 'Phone Number')} 
                    value={profileForm.phoneNumber || ''} 
                    onChangeText={(text) => {
                      // Keep only digits, limit to 15
                      const digitsOnly = (text || '').replace(/\D/g, '').slice(0, 15);
                      handleProfileInputChange('phoneNumber', digitsOnly);
                    }} 
                    keyboardType="phone-pad" 
                    style={[styles.input, { flex: 1 }]}
                    mode="flat"
                    maxLength={15}
                  />
                </View>
                {profileErrors.phoneNumber && (
                  <Text style={{ color: theme.colors.error, marginTop: -8 }}>{profileErrors.phoneNumber}</Text>
                )}
                <CountryCodeSelector
                  visible={phoneMenuVisible}
                  onDismiss={() => setPhoneMenuVisible(false)}
                  onSelect={(code) => { setCountryCode(code); setPhoneMenuVisible(false); }}
                  selectedCode={countryCode}
                />
                {userData?.phone_verified === false && (
                  <>
                    <Button
                      mode="outlined"
                      onPress={() => {
                        const editedPhone = profileForm.phoneNumber ? `${countryCode}${profileForm.phoneNumber}` : null;
                        const targetPhone = editedPhone || userData?.phone_number || '';
                        router.push({ pathname: '/dashboard/phone-verification-request', params: { phone: targetPhone }});
                      }}
                      style={{ marginTop: 8 }}
                      disabled={!profileForm.phoneNumber && !userData?.phone_number}
                    >
                      {t('verifyPhone')}
                    </Button>

                    <Button
                      mode="text"
                      onPress={() => {
                        const editedPhone = profileForm.phoneNumber ? `${countryCode}${profileForm.phoneNumber}` : null;
                        const targetPhone = editedPhone || userData?.phone_number || '';
                        router.push({
                          pathname: '/dashboard/phone-verify-via-bot',
                          params: { whatsapp_number: targetPhone },
                        });
                      }}
                      style={{ marginBottom: 16 }}
                      disabled={!profileForm.phoneNumber && !userData?.phone_number}
                    >
                      Verificar via bot WhatsApp
                    </Button>
                  </>
                )}
                <CustomDatePicker
                  label={t('userData.dateOfBirth', 'Date of Birth (YYYY-MM-DD)')}
                  value={profileForm.dateOfBirth || ''}
                  onChange={(date) => handleProfileInputChange('dateOfBirth', date)}
                  maxDate={new Date().toISOString().split('T')[0]}
                />
                {profileErrors.dateOfBirth && (
                  <Text style={{ color: theme.colors.error, marginTop: -8 }}>{profileErrors.dateOfBirth}</Text>
                )}
                <TextInput 
                  label={t('userData.nif', 'NIF')} 
                  value={profileForm.nif || ''} 
                  onChangeText={(text) => handleProfileInputChange('nif', text)} 
                  keyboardType="numeric" 
                  maxLength={9} 
                  style={styles.input}
                  mode="flat"
                />
                {profileErrors.nif && (
                  <Text style={{ color: theme.colors.error, marginTop: -8 }}>{profileErrors.nif}</Text>
                )}
                <TextInput 
                  label={t('userData.address', 'Address')} 
                  value={profileForm.address || ''} 
                  onChangeText={(text) => handleProfileInputChange('address', text)} 
                  style={styles.input}
                  mode="flat"
                />
                {profileErrors.address && (
                  <Text style={{ color: theme.colors.error, marginTop: -8 }}>{profileErrors.address}</Text>
                )}
                {hasDriver && (
                  <>
                    <OperatorLicenseInput
                      label={t('licenseNumberPlaceholder', 'License Number')}
                      value={profileForm.licenseNumber || ''}
                      onChangeText={(text) => handleProfileInputChange('licenseNumber', text)}
                      style={styles.input}
                    />
                    {profileErrors.licenseNumber && (
                      <Text style={{ color: theme.colors.error, marginTop: -8 }}>{profileErrors.licenseNumber}</Text>
                    )}
                  </>
                )}
                {(profileErrors.non_field_errors && profileErrors.non_field_errors.length > 0) && (
                  <View style={{ marginTop: 8 }}>
                    {profileErrors.non_field_errors.map((msg, idx) => (
                      <Text key={`p-nfe-${idx}`} style={{ color: theme.colors.error }}>{msg}</Text>
                    ))}
                  </View>
                )}
                {(profileErrors.other && profileErrors.other.length > 0) && (
                  <View style={{ marginTop: 8 }}>
                    {profileErrors.other.map((msg, idx) => (
                      <Text key={`p-other-${idx}`} style={{ color: theme.colors.error }}>{msg}</Text>
                    ))}
                  </View>
                )}
                <Button mode="contained" onPress={handleSaveProfile} loading={isSubmitting} disabled={isSubmitting} style={styles.button}>{t('save', 'Save')}</Button>
                <Button mode="outlined" onPress={() => setIsEditingProfile(false)} disabled={isSubmitting} style={styles.button}>{t('cancel', 'Cancel')}</Button>
              </>
            ) : (
              <>
                {renderDetailItem(t('userData.fullName', 'Full Name'), userData?.full_name ?? undefined)}
                {renderDetailItem(t('userData.email', 'Email'), userData?.email ?? undefined)}
                {userData?.phone_number
                  ? (
                    <Surface style={styles.detailItem} elevation={0}>
                      <Text variant="bodyLarge" style={styles.detailLabel}>{t('userData.phoneNumber', 'Phone Number')}:</Text>
                      <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
                        <Text variant="bodyLarge" style={styles.detailValue}>{userData?.phone_number}</Text>
                        <PhoneVerifiedBadge isVerified={userData?.phone_verified ?? false} size='small' />
                      </View>
                    </Surface>
                  )
                  : renderDetailItem(t('userData.phoneNumber', 'Phone Number'), userData?.phone_number ?? undefined)}
                {userData?.phone_verified === false && userData?.phone_number ? (
                  <Button
                    mode="text"
                    onPress={() =>
                      router.push({
                        pathname: '/dashboard/phone-verify-via-bot',
                        params: { whatsapp_number: userData.phone_number },
                      })
                    }
                    style={{ alignSelf: 'flex-start', marginTop: -6 }}
                  >
                    Verificar via bot WhatsApp
                  </Button>
                ) : null}
                {renderDetailItem(t('userData.dateOfBirth', 'Date of Birth'), userData?.profile?.date_of_birth ?? undefined)}
                {renderDetailItem(t('userData.nif', 'NIF'), userData?.profile?.nif ?? undefined)}
                {renderDetailItem(t('userData.address', 'Address'), userData?.profile?.address ?? undefined)}
                {hasDriver && renderDetailItem(t('licenseNumberPlaceholder', 'License Number'), userData?.profile?.license_number ?? undefined)}
                <Button mode="contained" onPress={() => { 
                    // Parse phone like load-time to avoid duplicating country code in input
                    const phoneRaw = userData?.phone_number || '';
                    let nextCountry = countryCode;
                    let localDigits = '';
                    if (phoneRaw && phoneRaw.startsWith('+')) {
                      const candidate = COUNTRY_CODES
                        .filter(code => phoneRaw.startsWith(code))
                        .sort((a, b) => b.length - a.length)[0];
                      if (candidate) {
                        nextCountry = candidate;
                        localDigits = phoneRaw.slice(candidate.length).replace(/\D/g, '').slice(0, 15);
                      } else {
                        const m = phoneRaw.match(/^(\+\d{1,4})(.*)$/);
                        nextCountry = m ? m[1] : '+';
                        localDigits = (m ? m[2] : phoneRaw.replace('+', '')).replace(/\D/g, '').slice(0, 15);
                      }
                    } else {
                      localDigits = (phoneRaw || '').replace(/\D/g, '').slice(0, 15);
                    }
                    setCountryCode(nextCountry);
                    const currentProfileData = {
                        fullName: userData?.full_name || '',
                        email: userData?.email || '',
                        phoneNumber: localDigits,
                        dateOfBirth: userData?.profile?.date_of_birth || '',
                        nif: userData?.profile?.nif || '',
                        address: userData?.profile?.address || '',
                        licenseNumber: userData?.profile?.license_number || '',
                    };
                    setProfileForm(currentProfileData); 
                    setIsEditingProfile(true);
                }} style={styles.buttonFullWidth}>{t('userData.editProfile', 'Edit Profile')}</Button>
              </>
            )}
          </Card.Content>
        </Card>

        {/* Change email section (#188): two-step flow — the account email
            only updates after the confirmation link sent to the new
            address is clicked. */}
        <Card style={[styles.card, { elevation: 0 }]}>
          <Card.Content>
            <Text variant="headlineMedium">{t('userData.changeEmailTitle', 'Change Email')}</Text>
            {emailChangeStatus?.pending ? (
              <>
                <Paragraph style={{ marginTop: 8 }}>
                  {t(
                    'userData.emailChangePendingMessage',
                    'We sent a confirmation link to {{email}}. Click it to finish changing your email.',
                    { email: emailChangeStatus.new_email }
                  )}
                </Paragraph>
                <Button
                  mode="outlined"
                  onPress={handleRequestEmailChange}
                  loading={isRequestingEmailChange}
                  disabled={isRequestingEmailChange || emailChangeCooldown > 0}
                  style={styles.buttonFullWidth}
                >
                  {emailChangeCooldown > 0
                    ? t('userData.resendInSeconds', 'Resend in {{seconds}}s', { seconds: emailChangeCooldown })
                    : t('userData.resendConfirmation', 'Resend confirmation email')}
                </Button>
              </>
            ) : (
              <>
                <Paragraph style={{ marginTop: 8 }}>
                  {t('userData.changeEmailHint', "You'll need to confirm your new email before it becomes active.")}
                </Paragraph>
                <TextInput
                  label={t('userData.newEmail', 'New email')}
                  value={newEmailInput}
                  onChangeText={(text) => {
                    setNewEmailInput(text);
                    if (emailChangeError) setEmailChangeError(undefined);
                  }}
                  keyboardType="email-address"
                  autoCapitalize="none"
                  error={!!emailChangeError}
                  style={styles.input}
                  mode="flat"
                />
                {emailChangeError && (
                  <Text style={{ color: theme.colors.error, marginTop: -8 }}>{emailChangeError}</Text>
                )}
                <Button
                  mode="contained"
                  onPress={handleRequestEmailChange}
                  loading={isRequestingEmailChange}
                  disabled={isRequestingEmailChange || emailChangeCooldown > 0 || !newEmailInput.trim()}
                  style={styles.buttonFullWidth}
                >
                  {emailChangeCooldown > 0
                    ? t('userData.resendInSeconds', 'Resend in {{seconds}}s', { seconds: emailChangeCooldown })
                    : t('userData.requestEmailChange', 'Request email change')}
                </Button>
              </>
            )}
          </Card.Content>
        </Card>

        {/* Change password section - both driver and owner */}
        <Card style={[styles.card, { elevation: 0 }]}>
          <Card.Content>
            <Text variant="headlineMedium">{t('changePasswordButton', 'Change Password')}</Text>
            <TextInput
              label={t('currentPasswordPlaceholder', 'Current password')}
              value={passwordForm.currentPassword}
              onChangeText={(text) => {
                setPasswordForm((prev) => ({ ...prev, currentPassword: text }));
                if (passwordFormErrors.currentPassword) setPasswordFormErrors((prev) => ({ ...prev, currentPassword: undefined }));
              }}
              secureTextEntry={!showCurrentPassword}
              mode="outlined"
              style={styles.input}
              error={!!passwordFormErrors.currentPassword}
              right={<TextInput.Icon icon={showCurrentPassword ? 'eye-off' : 'eye'} onPress={() => setShowCurrentPassword(!showCurrentPassword)} />}
            />
            {passwordFormErrors.currentPassword && (
              <Text style={{ color: theme.colors.error, marginTop: -8 }}>{passwordFormErrors.currentPassword}</Text>
            )}
            <TextInput
              label={t('newPasswordPlaceholder', 'New Password')}
              value={passwordForm.newPassword1}
              onChangeText={(text) => {
                setPasswordForm((prev) => ({ ...prev, newPassword1: text }));
                if (passwordFormErrors.newPassword1) setPasswordFormErrors((prev) => ({ ...prev, newPassword1: undefined }));
              }}
              secureTextEntry={!showNewPassword1}
              mode="outlined"
              style={styles.input}
              error={!!passwordFormErrors.newPassword1}
              right={<TextInput.Icon icon={showNewPassword1 ? 'eye-off' : 'eye'} onPress={() => setShowNewPassword1(!showNewPassword1)} />}
            />
            {passwordFormErrors.newPassword1 && (
              <Text style={{ color: theme.colors.error, marginTop: -8 }}>{passwordFormErrors.newPassword1}</Text>
            )}
            <TextInput
              label={t('confirmPasswordPlaceholder', 'Confirm New Password')}
              value={passwordForm.newPassword2}
              onChangeText={(text) => {
                setPasswordForm((prev) => ({ ...prev, newPassword2: text }));
                if (passwordFormErrors.newPassword2) setPasswordFormErrors((prev) => ({ ...prev, newPassword2: undefined }));
              }}
              secureTextEntry={!showNewPassword2}
              mode="outlined"
              style={styles.input}
              error={!!passwordFormErrors.newPassword2}
              right={<TextInput.Icon icon={showNewPassword2 ? 'eye-off' : 'eye'} onPress={() => setShowNewPassword2(!showNewPassword2)} />}
            />
            {passwordFormErrors.newPassword2 && (
              <Text style={{ color: theme.colors.error, marginTop: -8 }}>{passwordFormErrors.newPassword2}</Text>
            )}
            <Button mode="contained" onPress={handleChangePasswordSubmit} loading={isChangingPassword} disabled={isChangingPassword} style={styles.buttonFullWidth}>
              {t('changePasswordButton', 'Change Password')}
            </Button>
          </Card.Content>
        </Card>

        {/* Company Transfer Section - drivers only */}
        {hasDriver && (
          <Card style={[styles.card, { elevation: 0 }]}>
            <Card.Content>
              <Text variant="headlineMedium">{t('workCompany:change', 'Change work company')}</Text>
              <Button testID="user-data-change-company" mode="contained" onPress={() => router.push('/dashboard/work-company')} style={styles.buttonFullWidth}>{t('workCompany:change', 'Change work company')}</Button>
            </Card.Content>
          </Card>
        )}
      </ScrollView>





      <Snackbar
        visible={snackbarVisible}
        onDismiss={() => setSnackbarVisible(false)}
        duration={3000}
        action={{
          label: t('ok', 'OK'),
          onPress: () => setSnackbarVisible(false),
        }}
        wrapperStyle={isLargeScreen ? { left: 0, right: 0, bottom: 120, alignItems: 'center' } : undefined}
        style={isLargeScreen ? { maxWidth: 560, borderRadius: 8 } : undefined}
      >
        {snackbarMessage}
      </Snackbar>
    </Surface>
  );
};

const createStyles = (theme: any) => StyleSheet.create({
  centered: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: 16,
  },
  container: {
  },
  contentContainer: {
    padding: 16,
    paddingBottom: 32,
  },
  card: {
    marginBottom: 20,
    elevation: 3,
  },
  input: {
    marginBottom: 12,
  },
  inputModal: {
    marginBottom: 12,
  },
  button: {
    marginTop: 8,
    marginBottom: 4,
  },
  buttonFullWidth: {
    marginTop: 16,
  },
  detailItem: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingVertical: 10,
    borderBottomWidth: 1,
    borderBottomColor: theme.colors.outline,
    backgroundColor: 'transparent',
    elevation: 0,
  },
  detailLabel: {
    fontSize: 16,
    color: theme.colors.onSurfaceVariant,
  },
  detailValue: {
    fontSize: 16,
    color: theme.colors.onSurface,
    flexShrink: 1,
    textAlign: 'right',
  },
  switchRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingVertical: 8,
    marginBottom: 8,
    paddingHorizontal: 0,
  },
  switchContainerView: {
    justifyContent: 'space-between',
    backgroundColor: 'transparent',
  },
  sectionSubTitle: {
    fontSize: 16,
    fontWeight: 'bold',
    marginTop: 16,
    marginBottom: 8,
    color: theme.colors.primary,
  },
  modalLabel: {
    fontSize: 14,
    color: theme.colors.onSurfaceVariant,
    marginBottom: Platform.OS === 'ios' ? 4 : 2,
    marginTop: 8,
  },
  segmentedButtonModal: {
    minWidth: 300,
  },
  errorTextModal: {
    color: theme.colors.error,
    fontSize: 12,
    marginTop: -8,
    marginBottom: 8,
    marginLeft: 4,
  },
  segmentedButton: {
    marginVertical: 8,
  },
  languageButton: {
    minWidth: 80,
  },
  menuItem: {
    paddingVertical: 8,
  },
  pickerContainer: {
    borderWidth: 1,
    borderColor: theme.colors.outline,
    borderRadius: 4,
    marginBottom: 12,
  },
  picker: {
    height: 50,
    width: '100%',
  },
  buttonContent: {
    justifyContent: 'flex-start',
  },
  fuelTypeScroll: {
    marginBottom: 12,
  },
  fuelTypeOption: {
    paddingVertical: 8,
  },
  modalOverlay: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
  },
  calendarContainer: {
    backgroundColor: '#fff',
    borderRadius: 10,
    padding: 20,
    width: '80%',
    maxWidth: 400,
  },
  warningText: {
    fontSize: 12,
    marginTop: -8,
    marginBottom: 8,
    marginLeft: 4,
  },
});

export default UserDataScreen; 